import {
	BusyIndicator,
	Form,
	IconButton,
	TextInput
} from "client/Components";
import { useIsMounted } from "client/Hooks";
import { isNilOrWhiteSpace, setTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import styles from "./InlineCreationForm.module.less";

export type InlineCreationFormProps = {
	onCreate: (name: string) => Promise<TryResult<void>>;
};

export const InlineCreationForm = (props: InlineCreationFormProps): ReactElement => {
	const {
		onCreate
	} = props;

	const [value, setValue] = useState<string>("");
	const [hasFocus, setHasFocus] = useState<boolean>(false);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const isMounted: () => boolean = useIsMounted();

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			if (isNilOrWhiteSpace(value)) {
				return;
			}

			setIsBusy(true);

			try {
				const result: TryResult<void> = await onCreate(value);

				if (result.wasSuccessful) {
					setValue("");
				}
			} catch (e) {
				console.error(e);
			}

			if (!isMounted()) {
				return;
			}
			setIsBusy(false);
		},
		[
			isBusy,
			isMounted,
			onCreate,
			value
		]
	);

	return (
		<>
			<div
				{...setTrackingId("inline-creation")}
				className={styles.root}
			>
				{isBusy && (
					<BusyIndicator />
				)}

				<Form onSubmit={submitHandler} height="fill">
					<TextInput
						type="text"
						value={value}
						onChange={setValue}
						onBlur={() => setHasFocus(false)}
						placeholder={localize("_AddANewTask")}
						isBorderless
						height="fill"
						onFocus={() => setHasFocus(true)}
						hasNoPadding
						trackingId="todo-composer"
					/>
				</Form>

				{(value !== "" || hasFocus) && (
					<IconButton
						icon="check"
						shape="round"
						tooltip={localize("_Add")}
						onClick={submitHandler}
						trackingId="confirm"
						kind="transparent"
						isDisabled={isNilOrWhiteSpace(value)}
					/>
				)}
			</div>
		</>
	);
};
InlineCreationForm.displayName = "InlineCreationForm";
